import { Alert, Col, Input, Row, Select, Space, Spin, Table, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CompanyUserProfile } from '../../models/company-user-profile-interface';
import { UserRoles } from '../../constants/userRoles';
import { ColumnType, SortOrder } from 'antd/lib/table/interface';
import Title from 'antd/lib/typography/Title';
import CustomFilterDropdown from '../../components/CustomFilterDropdown';
import { Committee } from '../../models/committee-interface';
import { getSSOAccessTypeDescription, SSOAccessType } from '../../constants/ssoAccessType';

interface UsersTabProps {
  users: CompanyUserProfile[] | undefined;
  isLoading: boolean;
  samlEnabledForCompany: boolean | undefined;
  samlConfiguredForCompany: boolean | undefined;
}

interface UserColumn extends ColumnType<CompanyUserProfile> {
  hidden?: boolean;
}

export default function UsersTab({ users, isLoading, samlEnabledForCompany, samlConfiguredForCompany }: UsersTabProps) {
  const activeUsers = users?.filter((u) => u.active)?.length ?? 0;
  const [filteredUsers, setFilteredUsers] = useState<CompanyUserProfile[] | undefined>(users);
  const [searchString, setSearchString] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');

  useEffect(() => {
    let newlyFilteredUsers = users;

    //Filter by selected role
    if (selectedRole !== 'all') {
      const roleId = getRoleId(selectedRole);
      newlyFilteredUsers = newlyFilteredUsers?.filter(
        (u) => u.roleId === roleId
      );
      //Committee Admin and Standard user have some role id, so they have to be filtered further on isCommitteeAdmin flag
      if (selectedRole === 'committeeAdmin') {
        newlyFilteredUsers = newlyFilteredUsers?.filter(
          (u) => u.isCommitteeAdmin
        );
      } else if (selectedRole === 'standard') {
        newlyFilteredUsers = newlyFilteredUsers?.filter(
          (u) => !u.isCommitteeAdmin
        );
      }
    }

    //Filter by search string
    if (!!searchString?.length) {
      newlyFilteredUsers = newlyFilteredUsers?.filter((u) =>
        u.fullName.toLowerCase().includes(searchString.toLowerCase())
      );
    }

    //Filter by group
    if((selectedGroup !== "all")) {
      let groupID = Number(selectedGroup)
      newlyFilteredUsers = newlyFilteredUsers?.filter(u => u.committeeUsers.some(cu => cu.companyCommittee.id === groupID))
    }

    setFilteredUsers(newlyFilteredUsers);
  }, [selectedRole, searchString, users, selectedGroup]);

  const groupsList = ():Committee[] => {
    var groups:Committee[] = []

    if(!!users) {
      users.forEach(u => {
        u.committeeUsers.forEach(c => {
          if(!groups.some(g => g.id === c.companyCommittee.id)) {
            groups.push(c.companyCommittee)
          }
        })
      })
    }

    return groups.sort((a,b) => a.name.localeCompare(b.name))
  }

  const columns : UserColumn[] = [
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: {
        compare: (a: { fullName: string }, b: { fullName: string }) =>
          a.fullName.localeCompare(b.fullName),
      },
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    },
    {
      title: 'Role',
      dataIndex: 'roleId',
      key: 'roleId',
      render: (roleId: number, user: CompanyUserProfile) => {
        return <span>{getRoleDescription(roleId, user)}</span>;
      },
      sorter: {
        compare: (a: { roleId: number }, b: { roleId: number }) =>
          a.roleId === b.roleId ? 0 : a.roleId > b.roleId ? -1 : 1,
      },
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    },
    {
      title: 'Email',
      dataIndex: 'emailAddress',
      key: 'emailAddress',
      render: (emailAddress: string, user: CompanyUserProfile) => {
        return <Link to={`/admin/user/${user.userId}`}>{emailAddress}</Link>;
      },
      sorter: {
        compare: (a: { emailAddress: string }, b: { emailAddress: string }) =>
          a.emailAddress.localeCompare(b.emailAddress),
      },
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, user: CompanyUserProfile) => {
        return <div>{active ? 'Yes' : 'No'}</div>;
      },
      sorter: {
        compare: (a: { active: boolean }, b: { active: boolean }) =>
          a.active.toString().localeCompare(b.active.toString()),
      },
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
      filters: boolFilters,
      onFilter: (value: string | any, record: CompanyUserProfile) => {
        const active = !!record.active ? 'Yes' : 'No';
        return active === value;
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        filters,
      }) => (
        <CustomFilterDropdown
          filters={filters}
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          dataIndex='activeFilter'
        />
      ),
    },
    {
      title: 'Notified',
      dataIndex: 'notificationDate',
      key: 'notificationDate',
      render: (notificationDate: string | null, user: CompanyUserProfile) => {
        return <div>{!!notificationDate ? 'Yes' : 'No'}</div>;
      },
      sorter: {
        compare: (
          a: { notificationDate: string | null },
          b: { notificationDate: string | null }
        ) =>
          (!!a.notificationDate)
            .toString()
            .localeCompare((!!b.notificationDate).toString()),
      },
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
      filters: boolFilters,
      onFilter: (value: string | any, record: CompanyUserProfile) => {
        const notified = !!record.notificationDate ? 'Yes' : 'No';
        return notified === value;
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        filters,
      }) => (
        <CustomFilterDropdown
          filters={filters}
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          dataIndex='notifiedFilter'
        />
      ),
    },
    {
      title: 'Logged',
      dataIndex: 'lastLoginDate',
      key: 'lastLoginDate',
      render: (lastLoginDate: string | null, user: CompanyUserProfile) => {
        return <div>{!!lastLoginDate ? 'Yes' : 'No'}</div>;
      },
      sorter: {
        compare: (
          a: { lastLoginDate: string | null },
          b: { lastLoginDate: string | null }
        ) =>
          (!!a.lastLoginDate)
            .toString()
            .localeCompare((!!b.lastLoginDate).toString()),
      },
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
      filters: boolFilters,
      onFilter: (value: string | any, record: CompanyUserProfile) => {
        const loggedIn = !!record.lastLoginDate ? 'Yes' : 'No';
        return loggedIn === value;
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        filters,
      }) => (
        <CustomFilterDropdown
          filters={filters}
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          dataIndex='loggedInFilter'
        />
      ),
    },
    {
        title: "Single Sign-On",
        dataIndex: "ssoAccessType",
        key: "ssoAccessType",
        hidden: !samlConfiguredForCompany,
        render: (ssoAccessType: SSOAccessType, record: CompanyUserProfile) => samlEnabledForCompany ? 
          <div>{getSSOAccessTypeDescription(ssoAccessType)}</div> : 
          <Tooltip overlay={`Single Sign-On is disabled for ${record.companyName}`}>
              <div>{getSSOAccessTypeDescription(ssoAccessType)}</div>
          </Tooltip>,
        sorter: {
            compare: (a: CompanyUserProfile, b: CompanyUserProfile) => 
                a.ssoAccessType - b.ssoAccessType
        },
        sortDirections: ["ascend", "descend", "ascend"],
        filters: [
            {
                text: getSSOAccessTypeDescription(SSOAccessType.SSOOnly),
                value: SSOAccessType.SSOOnly
            },
            {
                text: getSSOAccessTypeDescription(SSOAccessType.GovendaLoginOnly),
                value: SSOAccessType.GovendaLoginOnly
            },
            {
                text: getSSOAccessTypeDescription(SSOAccessType.SSOAndGovendaLogin),
                value: SSOAccessType.SSOAndGovendaLogin
            }
        ],
        onFilter: (value: SSOAccessType | any, record: CompanyUserProfile) => record?.ssoAccessType === value,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) => (
            <CustomFilterDropdown
                filters={filters}
                setSelectedKeys={setSelectedKeys}
                selectedKeys={selectedKeys}
                confirm={confirm}
                clearFilters={clearFilters}
                dataIndex="ssoAccessType"
            />
        ),
        width: "150px"
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <Space direction='vertical' size='large' style={{ display: 'flex' }}>
        <Row>
          <Col span={24}>
            <Alert
              type='info'
              message={
                <>
                  <b>{`${activeUsers} active`}</b>
                  {` out of ${users?.length ?? 0} total users`}
                </>
              }
            />
          </Col>
        </Row>
        <Row>
          <Col flex='auto'>
            <Title level={3}>Users</Title>
          </Col>
          <Col>
            <Space>
              <Select
                value={selectedRole}
                onChange={(val) => {
                  setSelectedRole(val);
                }}
                style={{ width: 165 }}
              >
                <Select.Option key='all' value='all'>All Users</Select.Option>
                <Select.Option key='super' value='super'>Super Users</Select.Option>
                <Select.Option key='admin' value='admin'>Admins</Select.Option>
                <Select.Option key='committeeAdmin' value='committeeAdmin'>
                  Committee Admins
                </Select.Option>
                <Select.Option key='standard' value='standard'>Standard Users</Select.Option>
              </Select>
              <Select value={selectedGroup} style={{minWidth: 175}} onChange={(value:string) => setSelectedGroup(value)}>
                <Select.Option key={"all"} value="all">All Groups</Select.Option>
                {groupsList().map(x => <Select.Option key={`${x.id}`} value={`${x.id}`}>{x.name}</Select.Option>)}
              </Select>
              <Input
                style={{ width: 200 }}
                onChange={(e) => {
                  setSearchString(e?.target?.value ?? null);
                }}
                value={searchString}
                placeholder={'Filter by Name'}
                allowClear
              />
            </Space>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Table
              id={'companyUsersTable'}
              columns={columns.filter(c => !c.hidden)}
              dataSource={filteredUsers}
              rowKey={'userId'}
              pagination={false}
              size='small'
              scroll={{ x: true }}
            />
          </Col>
        </Row>
      </Space>
    </Spin>
  );
}

const getRoleDescription = (roleId: number, user: CompanyUserProfile) => {
  let description = '';
  switch (roleId) {
    case UserRoles.Super:
      description = 'Super User';
      break;
    case UserRoles.Admin:
      description = 'Admin';
      break;
    case UserRoles.Standard:
      if (user.isCommitteeAdmin) description = 'Committee Admin';
      else description = 'Standard';
      break;
    default:
      description = 'Standard';
      break;
  }
  return description;
};

const getRoleId = (roleKey: string) => {
  let roleId = 0;
  switch (roleKey) {
    case 'standard':
      roleId = 1;
      break;
    case 'committeeAdmin':
      roleId = 1;
      break;
    case 'super':
      roleId = 2;
      break;
    case 'admin':
      roleId = 3;
      break;
    default:
      roleId = 0;
      break;
  }
  return roleId;
};

const boolFilters: { value: string; text: string }[] = [
  {
    value: 'Yes',
    text: 'Yes',
  },
  {
    value: 'No',
    text: 'No',
  },
];
